import './NewsletterPage.scss'

import {BlockType, Blocks} from '../blocks/Blocks'
import {
	Contentside,
	ContentsideContent,
	ContentsideSidebar
} from '../layout/Contentside'
import {
	FormButton,
	FormButtons,
	FormField,
	FormFields,
	FormGroup
} from '../layout/Form'
import {FormProvider, useForm} from 'react-hook-form'
import {Hero, HeroSide, HeroType} from '../layout/Hero'
import {LinkType, PageData, WysiwygType} from '../../types'
import React, {useState} from 'react'

import {Breadcrumbs} from '../layout/Breadcrumbs'
import {Layout} from '../layout/Layout'
import {SidebarBlocks} from '../sidebar/SidebarBlocks'
import {Theme} from '../Theme'
import {fetcher} from '../../util/fetcher'
import {style} from '../../util/style'
import {useTranslation} from '../../util/i18n'
import {wrap} from '@codeurs/front'

export type NewsletterType = {
	hero: HeroType
	blocks?: Array<BlockType>
}
const styles = style('newsletterpage', ['form', 'thanks'])

export const NewsletterPage: React.FC<PageData & NewsletterType> = wrap(Layout)(
	({...props}) => {
		const {
			parents,
			title,
			hero,
			form_privacy,
			form_thanks_title,
			form_thanks_text,
			content_bgcolor,
			contentblocks,
			sidebar_overflow,
			sidebarblocks,
			blocks
		} = props
		const hasContentside =
			(hero && !hero?.image) ||
			(contentblocks && contentblocks.length > 0) ||
			(sidebarblocks && sidebarblocks.length > 0)
		const hasSidebar = sidebarblocks && sidebarblocks.length > 0

		return (
			<div className={styles()}>
				{hero?.image && (
					<Hero {...hero} title={(hero && hero.title) || title} />
				)}
				<Breadcrumbs
					parents={parents}
					title={title}
					padright={sidebar_overflow}
				/>
				{hasContentside && (
					<Contentside bgcolor={content_bgcolor}>
						<ContentsideContent>
							{(!hero || !hero?.image) && (
								<HeroSide {...hero} title={hero?.title || title} />
							)}
							<Blocks blocks={contentblocks} />
						</ContentsideContent>
						{hasSidebar && (
							<ContentsideSidebar
								overflow={sidebar_overflow && hero?.image?.src}
							>
								<SidebarBlocks blocks={sidebarblocks} />
							</ContentsideSidebar>
						)}
					</Contentside>
				)}
				<NewsletterForm
					form_privacy={form_privacy}
					form_thanks_title={form_thanks_title}
					form_thanks_text={form_thanks_text}
				/>
				<Blocks blocks={blocks} container={true} />
			</div>
		)
	}
)

const NewsletterForm: React.FC<{
	form_privacy: LinkType
	form_thanks_title: WysiwygType
	form_thanks_text: WysiwygType
}> = ({form_privacy, form_thanks_title, form_thanks_text}) => {
	const t = useTranslation()
	const methods = useForm()
	const lang: string = 'nl'
	const {setError, handleSubmit} = methods
	const [isSending, setIsSending] = useState<boolean>(false)
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
	const onSubmit = (formData, e) => {
		e.preventDefault()
		setIsSending(true)

		const fd = new FormData()
		Object.keys(formData).forEach((k) => {
			fd.append(k, formData[k])
		})

		fetcher(`/api/subscribetonewsletter`, {
			method: 'POST',
			body: fd
		})
			.then((res: {messages: Array<string>}) => {
				// success
				// example success response:
				// const res = {messages: ['Successfully subscribed']}
				setIsSending(false)
				setIsSubmitted(true)
			})
			.catch((err: {[key: string]: Array<string>}) => {
				// error
				// example error response:
				// const err = {fname: ['Gelieve dit veld in te vullen.']}
				// const err = {messages: ['Mailchimp error text']}
				Object.keys(err).forEach((errorKey: string) => {
					setError(errorKey, {
						type: 'validate',
						message: JSON.stringify(err[errorKey])
					})
				})
				setIsSending(false)
			})
	}

	return (
		<FormProvider {...methods}>
			{!isSubmitted ? (
				<form onSubmit={handleSubmit(onSubmit)} className={styles.form()}>
					<Theme.Container>
						<FormGroup button={true}>
							<FormFields>
								<FormField
									amount={null}
									name="language"
									value={lang}
									hidden={true}
									mod={['hidden']}
								/>
								<FormField
									amount={null}
									required
									name="fname"
									label={t.newsletterpage.fname}
									mod={['half', 'maxwidth']}
								/>
								<FormField
									amount={null}
									required
									name="lname"
									label={t.newsletterpage.lname}
									mod={['half', 'maxwidth']}
								/>
								<FormField
									required
									type="email"
									name="email"
									label={t.newsletterpage.email}
									mod={['half', 'maxwidth']}
								/>
								<FormField
									type="tel"
									name="phone"
									label={t.newsletterpage.phone}
									mod={['half', 'maxwidth']}
								/>
								<FormField
									amount={null}
									required
									type="checkbox"
									name="agreement_privacy_policy"
									label={
										t.newsletterpage.agreement_privacy_policy +
										(form_privacy?.url
											? ` <a href="${form_privacy?.url}" target="_blank">${
													form_privacy?.description || form_privacy?.title
											  }</a>. (*)`
											: ' privacy policy. (*)')
									}
									mod={['maxwidth']}
								/>
							</FormFields>
							<Theme.H3 className={styles.form('title')}>
								{t.newsletterpage.refine_title}
							</Theme.H3>
							<div className={styles.form('region')}>
								<Theme.H4 className={styles.form('region-subtitle')}>
									{t.newsletterpage.region_title}
								</Theme.H4>
								<FormFields>
									<FormField
										amount={null}
										type="checkbox"
										name="regio_antwerpen"
										label={t.newsletterpage.regio_antwerpen}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="regio_oost_vlaanderen"
										label={t.newsletterpage.regio_oost_vlaanderen}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="regio_limburg"
										label={t.newsletterpage.regio_limburg}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="regio_vlaams_brabant"
										label={t.newsletterpage.regio_vlaams_brabant}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="regio_west_vlaanderen"
										label={t.newsletterpage.regio_west_vlaanderen}
										mod={['half']}
									/>
								</FormFields>
							</div>
							<div className={styles.form('interests')}>
								<Theme.H4 className={styles.form('interests-subtitle')}>
									{t.newsletterpage.interests_title}
								</Theme.H4>
								<FormFields>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_nieuwsbrief"
										label={t.newsletterpage.interesses_nieuwsbrief}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_meegedeeld"
										label={t.newsletterpage.interesses_meegedeeld}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_sportieve_evenementen"
										label={t.newsletterpage.interesses_sportieve_evenementen}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_evenementen_bd"
										label={t.newsletterpage.interesses_evenementen_bd}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_lager_onderwijs"
										label={t.newsletterpage.interesses_lager_onderwijs}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_secundair_onderwijs"
										label={t.newsletterpage.interesses_secundair_onderwijs}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_jongereninfo"
										label={t.newsletterpage.interesses_jongereninfo}
										mod={['half']}
									/>
									<FormField
										amount={null}
										type="checkbox"
										name="interesses_lezingen_debatten"
										label={t.newsletterpage.interesses_lezingen_debatten}
										mod={['half']}
									/>
								</FormFields>
							</div>
							<FormButtons overflow={true}>
								<FormButton>
									<Theme.Button
										as="button"
										loading={isSending}
										disabled={isSubmitted}
									>
										{t.newsletterpage.submit}
									</Theme.Button>
								</FormButton>
							</FormButtons>
						</FormGroup>
					</Theme.Container>
				</form>
			) : (
				<NewsletterThanks
					form_thanks_title={form_thanks_title}
					form_thanks_text={form_thanks_text}
				/>
			)}
		</FormProvider>
	)
}

const NewsletterThanks: React.FC<{
	form_thanks_title: WysiwygType
	form_thanks_text: WysiwygType
}> = ({form_thanks_title, form_thanks_text}) => {
	const t = useTranslation()

	return (
		<Theme.Container>
			<div className={styles.thanks()}>
				<Theme.H2 className={styles.thanks('title')}>
					{form_thanks_title || t.newsletterpage.thanks_title}
				</Theme.H2>
				{form_thanks_text && (
					<div className={styles.thanks('text')}>
						<Theme.Wysiwyg>{form_thanks_text}</Theme.Wysiwyg>
					</div>
				)}
			</div>
		</Theme.Container>
	)
}
